import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { LOCALES, DEFAULT_LOCALE } from './pick';
import { STRINGS } from './strings';

const LocaleContext = createContext();
const STORAGE_KEY = 'locale';

function resolveInitialLocale() {
  if (typeof window === 'undefined') return DEFAULT_LOCALE;
  // 1) explicit ?lang= override (shareable / SEO), 2) saved choice, 3) browser, 4) default
  const fromUrl = new URLSearchParams(window.location.search).get('lang');
  if (fromUrl && LOCALES.includes(fromUrl)) return fromUrl;
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && LOCALES.includes(stored)) return stored;
  const browser = (navigator.language || '').slice(0, 2).toLowerCase();
  if (LOCALES.includes(browser)) return browser;
  return DEFAULT_LOCALE;
}

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(resolveInitialLocale);

  const setLocale = useCallback((next) => {
    if (!LOCALES.includes(next)) return;
    setLocaleState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore storage failures (private mode, etc.) */
    }
  }, []);

  // Keep <html lang> in sync for accessibility + SEO.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const value = {
    locale,
    setLocale,
    t: STRINGS[locale],
  };

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within a LocaleProvider');
  return ctx;
}
